import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onMount, tick } from "svelte";
import { ConfigParser } from "../adapters/config/config-parser.js";
import { CompositeDepartureRepository } from "../adapters/composite-departure-repository.js";
import { nearbySourceFor } from "../adapters/on-the-run-nearby-source.js";
import { LocalStorageCache } from "../adapters/storage/local-storage-cache.js";
import { LocalStorageConfigStorage } from "../adapters/storage/local-storage-config-storage.js";
import { DepartureGroupingService } from "../application/services/departure-grouping-service.js";
import { determineApiStatus } from "../application/services/api-status.js";
import { updateFavicon } from "../application/services/favicon-service.js";
import { MultiStopPoller } from "../application/services/multi-stop-poller.js";
import { OnTheRunPoller } from "../application/services/on-the-run-poller.js";
import { calculateFillVerticalSpace, setFontSizesFromConfig } from "../utils/font-scaling.js";
import { fetchConfigTomlFromSearch } from "../utils/config-url.js";
import { shouldShowFailureNotice } from "../utils/failure-message.js";
import { initDestinationScrolling, initRouteNumberScrolling } from "../utils/destination-scrolling.js";
import { initTimeFormatToggle, cleanupTimeFormatToggle } from "../utils/time-format-toggle.js";
import { retryPolicyFromDisplay } from "../utils/retry.js";
import ConfigModal from "./ConfigModal.svelte";
import DeparturesList from "./DeparturesList.svelte";
import StatusBar from "./StatusBar.svelte";
import StopFailures from "./StopFailures.svelte";

var root_1 = $.from_html(`<div role="main" aria-label="MVG Departures Dashboard"><div class="header-section"><h1> </h1> <div class="last-update" aria-live="polite" aria-atomic="true"><!></div></div> <!> <div id="departures" role="region" aria-label="Departure information" aria-live="polite" aria-atomic="false"><!></div> <!> <!></div>`);

export default function App($$anchor, $$props) {
	$.push($$props, true);

	let config = $.state(null);
	let currentRoute = $.state(null);
	let groupedDepartures = $.state($.proxy([]));
	let showConfigModal = $.state(false);
	let apiStatus = $.state("unknown");
	let stopFailures = $.state($.proxy([]));
	let lastUpdateTime = $.state(null);
	let refreshIntervalSeconds = $.state(20);

	/** The running cycle's progress; `null` once it is over. */
	let pollProgress = $.state(null);

	let isFetching = $.derived(() => $.get(pollProgress) !== null);

	// eslint-disable-next-line svelte/prefer-svelte-reactivity -- Set is reactive in Svelte 5 with proper usage
	let activePollers = new Set(); // Track all active pollers

	let unsupportedProviders = $.state($.proxy([]));
	let isPageSuspended = $.state(false);
	let onTheRunConfig = $.state(null);
	let onTheRunPoller = $.state(null);
	let onTheRunStatusMessages = $.state($.proxy([]));
	let showFailureNotice = $.derived(() => shouldShowFailureNotice($.get(stopFailures), $.get(groupedDepartures)));
	const ON_THE_RUN_ROUTE = "on-the-run";

	function formatDate(date) {
		// Format date as YYYY-MM-DD (e.g., "2026-01-21")
		const year = date.getFullYear();

		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");

		return `${year}-${month}-${day}`;
	}

	function collectConfiguredStops(appConfig) {
		if (!appConfig) {
			return [];
		}

		return appConfig.routes.flatMap((route) => route.stops ?? []);
	}

	/** The route the app opens with: the one at "/", or the first configured. */
	function defaultRoute(routes) {
		return routes.find((route) => route.path === "/") ?? routes[0];
	}

	const configStorage = new LocalStorageConfigStorage();
	const configParser = new ConfigParser();
	const cache = new LocalStorageCache();

	// departureRepository will be created per route based on stop configs
	let departureRepository = null;

	let groupingService = null;

	// Initialize all - matches Python version's initializeAll() function
	function initializeAll() {
		// Initialize time format toggle
		if ($.get(currentRoute)?.display?.timeFormatToggleSeconds !== undefined) {
			initTimeFormatToggle($.get(currentRoute).display.timeFormatToggleSeconds);
		}

		// Calculate dynamic font sizes if fill_vertical_space is enabled
		// This matches Python: if (window.DEPARTURES_CONFIG && window.DEPARTURES_CONFIG.fillVerticalSpace) { requestAnimationFrame(() => { calculateFillVerticalSpace(); }); }
		if ($.get(currentRoute)?.display?.fillVerticalSpace && $.get(groupedDepartures).length > 0) {
			// Note: This is called from within requestAnimationFrame already, so we can call directly
			// The outer requestAnimationFrame ensures DOM is ready
			calculateFillVerticalSpace({
				fillVerticalSpace: true,
				fontScalingFactorWhenFilling: $.get(currentRoute)?.display?.fontScalingFactorWhenFilling ?? 1.0
			});

			// Wait for layout to complete after font scaling (matches Python view pattern)
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					initDestinationScrolling();
				});
			});
		} else if (!$.get(currentRoute)?.display?.fillVerticalSpace && $.get(groupedDepartures).length > 0) {
			// When fillVerticalSpace is disabled, still set font sizes from config to prevent overlap
			// Note: This is called from within requestAnimationFrame already, so we can call directly
			// The outer requestAnimationFrame ensures DOM is ready
			setFontSizesFromConfig($.get(currentRoute)?.display);

			// Wait for layout to complete after font size changes (matches Python view pattern)
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					initDestinationScrolling();
				});
			});
		} else {
			// No font scaling, but still need to check for clipped destinations
			requestAnimationFrame(() => {
				initDestinationScrolling();
			});
		}
	}

	onMount(() => {
		let cleanupVisibility;
		let cleanupResize;
		let cleanupHash;

		const setup = async () => {
			await loadConfig();

			// Initialize CSS variables for font sizes (even if fillVerticalSpace is disabled)
			// Set default values from CSS
			const root = document.documentElement;

			if (!root.style.getPropertyValue("--font-size-route-number")) {
				// Initialize with default rem values (will be overridden by fillVerticalSpace if enabled)
				root.style.setProperty("--font-size-route-number", "4rem");

				root.style.setProperty("--font-size-destination", "4rem");
				root.style.setProperty("--font-size-platform", "2.5rem");
				root.style.setProperty("--font-size-time", "4rem");
				root.style.setProperty("--font-size-direction-header", "4rem");
				root.style.setProperty("--font-size-stop-header", "3rem");
				root.style.setProperty("--font-size-no-departures", "2.5rem");
				root.style.setProperty("--font-size-pagination-indicator", "2rem");
				root.style.setProperty("--font-size-countdown-text", "1.8rem");
				root.style.setProperty("--font-size-delay-amount", "2rem");
				root.style.setProperty("--font-size-status-header", "4rem");
			}

			await initializeRoute();

			// Listen for hash changes (hash-based routing)
			window.addEventListener("hashchange", handleHashChange);

			cleanupHash = () => {
				window.removeEventListener("hashchange", handleHashChange);
			};

			// Listen for window resize to recalculate font sizes (matches Python exactly)
			// Handle window resize for fill_vertical_space (matches Python: lines 1426-1436)
			// Skip resize events caused by pinch-zoom to prevent re-layout during zoom
			let resizeTimeout = null;

			let lastLayoutWidth = window.innerWidth;
			let lastLayoutHeight = window.innerHeight;

			const handleResize = () => {
				if ($.get(currentRoute)?.display?.fillVerticalSpace) {
					// Only recalculate if the layout viewport actually changed
					// Pinch-zoom changes visualViewport but not layout viewport (innerWidth/innerHeight)
					const currentWidth = window.innerWidth;

					const currentHeight = window.innerHeight;

					if (currentWidth === lastLayoutWidth && currentHeight === lastLayoutHeight) {
						// Layout viewport unchanged - this is likely just a zoom, skip recalculation
						return;
					}

					// Update last known dimensions
					lastLayoutWidth = currentWidth;

					lastLayoutHeight = currentHeight;

					// Debounce resize events (matches Python: 150ms)
					if (resizeTimeout) clearTimeout(resizeTimeout);

					resizeTimeout = window.setTimeout(
						() => {
							calculateFillVerticalSpace({
								fillVerticalSpace: true,
								fontScalingFactorWhenFilling: $.get(currentRoute)?.display?.fontScalingFactorWhenFilling ?? 1.0
							});
						},
						150
					);
				}
			};

			window.addEventListener("resize", handleResize);

			cleanupResize = () => {
				if (resizeTimeout) {
					clearTimeout(resizeTimeout);
				}

				window.removeEventListener("resize", handleResize);
			};

			// Detect when page/tab is suspended/hidden (Page Visibility API only)
			// When suspended, grey out all rows to signal staleness
			// Main use case: browser suspends tab timers when tab is hidden, data becomes stale
			async function handleVisibilityChange() {
				const wasSuspended = $.get(isPageSuspended);

				// Only set stale when page is actually hidden (tab suspended)
				// Don't set stale on focus loss - only when browser suspends timers
				if (document.hidden) {
					$.set(isPageSuspended, true);

					if (!wasSuspended) {
						console.log("Page SUSPENDED (tab hidden/device sleeping) - marking data as stale");
					}
				} else if (!document.hidden && wasSuspended && activePollers.size > 0) {
					// Page became visible again - force immediate refresh to get fresh data
					console.log("Page VISIBLE again (tab shown/device woke up) - forcing immediate refresh");

					try {
						// Refresh all active pollers
						for (const poller of activePollers) {
							await poller.refreshNow();
						}
					} catch(error) {
						console.error("Error during forced refresh:", error);
					}
				}
			}

			// Check initial state
			if (document.hidden) {
				$.set(isPageSuspended, true);
			}

			// Listen for visibility changes (Page Visibility API)
			document.addEventListener("visibilitychange", handleVisibilityChange);

			cleanupVisibility = () => {
				document.removeEventListener("visibilitychange", handleVisibilityChange);
			};
		};

		void setup();

		return () => {
			cleanupVisibility?.();
			cleanupResize?.();
			cleanupHash?.();
		};
	});

	// Recalculate font sizes after departures update
	// This matches Python's phx:update handler: requestAnimationFrame(() => { calculateFillVerticalSpace(); })
	$.user_effect(() => {
		// Access reactive values to trigger effect (intentionally unused to trigger reactivity)
		void $.get(groupedDepartures);

		void $.get(currentRoute);
		void handleDeparturesUpdate();
	});

	async function handleDeparturesUpdate() {
		if ($.get(currentRoute)?.display?.fillVerticalSpace && $.get(groupedDepartures).length > 0) {
			// Wait for Svelte to finish rendering
			await tick();

			// Use requestAnimationFrame to ensure DOM is fully laid out (matches Python exactly)
			requestAnimationFrame(() => {
				console.log("Recalculating font sizes (fillVerticalSpace enabled)");

				calculateFillVerticalSpace({
					fillVerticalSpace: true,
					fontScalingFactorWhenFilling: $.get(currentRoute)?.display?.fontScalingFactorWhenFilling ?? 1.0
				});

				// Initialize time format toggle (will reinitialize if already running)
				initTimeFormatToggle($.get(currentRoute)?.display?.timeFormatToggleSeconds ?? 0);

				// Wait for layout to complete after font scaling (matches Python view pattern)
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						initRouteNumberScrolling();
						initDestinationScrolling();
					});
				});
			});
		} else if (!$.get(currentRoute)?.display?.fillVerticalSpace) {
			console.log("fillVerticalSpace is disabled, setting font sizes from config");

			// When fillVerticalSpace is disabled, still set font sizes from config and ensure proper line-heights
			// This prevents font overlap (matches Python: CSS variables are always set)
			await tick();

			requestAnimationFrame(() => {
				setFontSizesFromConfig($.get(currentRoute)?.display);
				initTimeFormatToggle($.get(currentRoute)?.display?.timeFormatToggleSeconds ?? 0);

				// Wait for layout to complete after font size changes (matches Python view pattern)
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						initRouteNumberScrolling();
						initDestinationScrolling();
					});
				});
			});
		}
	}

	function handleHashChange() {
		const hash = window.location.hash.slice(1); // Remove leading #

		if (!$.get(config) || $.get(config).routes.length === 0) {
			return;
		}

		const route = $.get(config).routes.find((r) => r.path === hash || hash === "" && r.path === "/");

		if (route) {
			switchRoute(route, false); // false = don't update hash (hash already changed)
		}
	}

	async function loadConfig() {
		try {
			const urlConfig = await fetchConfigTomlFromSearch(window.location.search);

			if (urlConfig) {
				const parsedFromUrl = configParser.parseToml(urlConfig.toml);

				await configStorage.saveConfig(parsedFromUrl);
				await configStorage.saveConfigToml(urlConfig.toml);
				console.log(`Loaded config from URL parameter and persisted it: ${urlConfig.configUrl}`);
			}
		} catch(error) {
			console.error("Failed to load config from URL parameter:", error);
		}

		// Always prefer TOML as source of truth (ensures undefined values are preserved correctly)
		// JSON serialization can lose undefined values or preserve incorrect false values
		const tomlString = await configStorage.getConfigToml();

		let stored = null;

		if (tomlString) {
			try {
				stored = configParser.parseToml(tomlString);

				// Save the parsed version for faster access next time (though we'll always parse from TOML)
				await configStorage.saveConfig(stored);

				console.log("Parsed TOML config (source of truth)");
			} catch(error) {
				console.error("Failed to parse stored TOML config:", error);
			}
		}

		// Fallback to parsed JSON only if TOML is not available
		if (!stored) {
			stored = await configStorage.getConfig();

			if (stored) {
				console.log("Loaded config from cached JSON (TOML not available)");
			}
		}

		// If no config found, load example config from static resources
		if (!stored) {
			try {
				// Use relative path to work with both root and subdirectory deployments
				const configPath = "./config.example.toml";

				const response = await fetch(configPath);

				if (response.ok) {
					const exampleToml = await response.text();

					stored = configParser.parseToml(exampleToml);

					// Store the example config in localStorage so it persists
					await configStorage.saveConfig(stored);

					await configStorage.saveConfigToml(exampleToml);
					console.log(`Loaded example config from ${configPath} and stored in localStorage`);
				} else {
					console.warn("Failed to fetch example config:", response.status, response.statusText);
				}
			} catch(error) {
				console.error("Failed to load example config:", error);
			}
		}

		if (stored) {
			$.set(config, stored, true);
			$.set(onTheRunConfig, stored.onTheRun ?? null, true);
			console.log(`Loaded config with ${stored.routes.length} route(s)`);

			stored.routes.forEach((route, idx) => {
				console.log(`  Route ${idx + 1}: path="${route.path}", ${route.stops.length} stop(s), fillVerticalSpace=${route.display?.fillVerticalSpace ?? false}`);
			});
		} else {
			console.log("No config found in localStorage and failed to load example config");
		}
	}

	async function initializeRoute() {
		if (!$.get(config) || $.get(config).routes.length === 0) {
			return;
		}

		// Get current route from hash (hash-based routing for SPA)
		const hash = window.location.hash.slice(1); // Remove leading #

		// If hash is empty or just "/", use the route at "/" (or the first one) but don't change the URL
		let route;

		if (hash === "" || hash === "/") {
			route = defaultRoute($.get(config).routes);

			// Use the route but don't set the hash - keep URL at root
			if (route) {
				await switchRoute(route, false); // false = don't update hash
			}

			return;
		} else {
			route = $.get(config).routes.find((r) => r.path === hash);
		}

		// Fallback to first route if not found
		if (!route) {
			route = $.get(config).routes[0];
		}

		if (route) {
			await switchRoute(route, true); // true = update hash (user navigated)
		}
	}

	async function switchRoute(route, updateHash = true) {
		console.log(`Switching to route: ${route.path} (${route.stops.length} stop(s))`);

		// Stop ALL existing pollers and clear the set
		console.log(`Stopping ${activePollers.size} active poller(s)`);

		for (const poller of activePollers) {
			poller.stop();
		}

		activePollers.clear();
		$.set(onTheRunPoller, null);

		// Cleanup time format toggle
		cleanupTimeFormatToggle();

		// Clear cache to prevent cross-route contamination
		// This is critical when routes share station IDs but have different configurations
		await cache.clear();

		console.log("Cache cleared when switching routes");

		// Clear existing departures and unsupported providers when switching routes
		$.set(groupedDepartures, [], true);

		$.set(unsupportedProviders, [], true);
		$.set(stopFailures, [], true);
		$.set(pollProgress, null);
		$.set(currentRoute, route, true);
		await configStorage.setCurrentRoutePath(route.path);
		$.set(onTheRunStatusMessages, [], true);

		// Update browser tab title and favicon (happens on initial load and when switching routes)
		const pageTitle = route.display?.title ?? "MVG Departures";

		document.title = pageTitle;
		updateFavicon(pageTitle);
		console.log(`Set page title to: ${pageTitle}`);

		// Only update hash if explicitly requested (user navigation, not initial load)
		if (updateHash) {
			// Use hash-based routing for SPA (don't use pushState with pathname)
			// Use hash to avoid server-side routing issues
			const hash = route.path === "/" ? "" : route.path;

			window.location.hash = hash;
		}

		const isOnTheRunRoute = route.isOnTheRun ?? route.path === ON_THE_RUN_ROUTE;

		if (isOnTheRunRoute && $.get(onTheRunConfig)) {
			$.set(onTheRunStatusMessages, ["Fetching location..."], true);
			await startOnTheRunPolling(route, $.get(onTheRunConfig));

			return;
		}

		// Start polling for all stops in route
		if (route.stops.length > 0) {
			console.log(`Starting poller for ${route.stops.length} stop(s):`, route.stops.map((s) => `${s.stationName} (${s.stationId}, api=${s.apiProvider ?? "mvg"})`));

			console.log(`Route display config:`, {
				randomHeaderColors: route.display?.randomHeaderColors,
				headerBackgroundBrightness: route.display?.headerBackgroundBrightness,
				fillVerticalSpace: route.display?.fillVerticalSpace,
				title: route.display?.title
			});

			// Create composite repository that routes to correct API per stop
			// This matches the Python version's CompositeDepartureRepository behavior
			departureRepository = new CompositeDepartureRepository(route.stops);

			$.set(unsupportedProviders, departureRepository.getUnsupportedProviders(), true);
			groupingService = new DepartureGroupingService(departureRepository);

			// Set API status to degraded if unsupported providers are detected
			if ($.get(unsupportedProviders).length > 0) {
				$.set(apiStatus, "degraded");
			}

			const refreshInterval = route.refreshIntervalSeconds ?? route.display?.refreshIntervalSeconds ?? 20;

			$.set(refreshIntervalSeconds, refreshInterval, true);

			const newPoller = new MultiStopPoller(
				departureRepository,
				cache,
				groupingService,
				route.stops,
				refreshInterval,
				{
					onUpdate: async (result, pollerId) => {
						// Only accept updates from pollers that are still in the active set
						if (!activePollers.has(newPoller)) {
							console.log(`[${pollerId}] Ignoring update from stopped poller (race condition prevented)`);

							return;
						}

						await applyCycleResult(result, pollerId);
					},

					onError: (error, pollerId) => {
						// Only accept errors from pollers that are still in the active set
						if (!activePollers.has(newPoller)) {
							console.log(`[${pollerId}] Ignoring error from stopped poller`);

							return;
						}

						console.error(`[${pollerId}] API poll error:`, error);
						$.set(pollProgress, null);
						$.set(apiStatus, "error");
					},

					onProgress: (progress) => {
						if (!activePollers.has(newPoller)) {
							return;
						}

						$.set(pollProgress, progress, true);
					}
				},
				route.display, // Pass route display config for header color inheritance
				retryPolicyFromDisplay(route.display)
			);

			// Add to active pollers set BEFORE starting
			activePollers.add(newPoller);

			await newPoller.start();

			// Call initializeAll after initial poll completes (matches Python's initializeAll on page load)
			// Use multiple requestAnimationFrame calls + small delay to ensure DOM is fully ready
			// This is critical on page reload (Ctrl+R) when DOM might be ready but layout not yet calculated
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					// Additional small delay to ensure layout is calculated (especially on page reload)
					setTimeout(
						() => {
							initializeAll();
						},
						50
					);
				});
			});
		}
	}

	/**
	 * Reflect one refresh cycle in the UI: the departures we got, the stops whose
	 * refresh failed, and the resulting API status.
	 * Shared by the regular and the on-the-run pollers.
	 */
	async function applyCycleResult(result, pollerId) {
		const departureCount = result.groups.reduce((sum, group) => sum + group.departures.length, 0);

		console.log(`[${pollerId}] Received ${result.groups.length} direction group(s) with ${departureCount} total departures, ${result.failures.length} failed stop(s)`);
		$.set(groupedDepartures, result.groups, true);
		$.set(stopFailures, result.failures, true);
		$.set(apiStatus, resolveApiStatus(result), true);

		// The cycle is over: the empty screen goes back to "no departures" instead
		// of claiming that calls are still running.
		$.set(pollProgress, null);

		// Clear stale state when new data arrives - fresh data means staleness is resolved
		if ($.get(isPageSuspended)) {
			console.log("New data received - clearing stale state");
			$.set(isPageSuspended, false);
		}

		$.set(lastUpdateTime, new Date(), true);

		// Wait for Svelte to update the DOM before calculating layout
		await tick();

		// Use multiple requestAnimationFrame calls + small delay to ensure DOM is fully laid out
		// This is critical for column width calculations to prevent overlap on first load
		// The delay ensures fonts are applied and layout is calculated before measuring
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setTimeout(
					() => {
						initializeAll();
					},
					50
				);
			});
		});
	}

	function resolveApiStatus(result) {
		const status = determineApiStatus(result.successCount, result.failures.length);

		// Unsupported providers are a permanent degradation, not a transient failure
		if (status === "success" && $.get(unsupportedProviders).length > 0) {
			return "degraded";
		}

		return status;
	}

	async function startOnTheRunPolling(route, onTheRunConfig) {
		console.log("Starting on-the-run polling");
		$.set(groupedDepartures, [], true);
		$.set(unsupportedProviders, [], true);
		$.set(stopFailures, [], true);
		$.set(onTheRunStatusMessages, ["Fetching location..."], true);
		$.set(apiStatus, "unknown");

		const refreshInterval = onTheRunConfig.updateLocationIntervalSeconds ?? 20;

		$.set(refreshIntervalSeconds, refreshInterval, true);

		const newPoller = new OnTheRunPoller(
			nearbySourceFor(onTheRunConfig.useAdapters),
			cache,
			onTheRunConfig,
			{
				onUpdate: async (result, pollerId) => {
					if (!activePollers.has(newPoller)) {
						console.log(`[${pollerId}] Ignoring update from stopped poller (race condition prevented)`);

						return;
					}

					await applyCycleResult(result, pollerId);
				},

				onError: (error, pollerId) => {
					if (!activePollers.has(newPoller)) {
						console.log(`[${pollerId}] Ignoring error from stopped poller`);

						return;
					}

					console.error(`[${pollerId}] On-the-run poll error:`, error);
					$.set(pollProgress, null);
					$.set(apiStatus, "error");
				},

				onUnsupportedProviders: (providers) => {
					$.set(unsupportedProviders, providers, true);
				},

				onStatusUpdate: (messages, pollerId) => {
					if (!activePollers.has(newPoller)) {
						console.log(`[${pollerId}] Ignoring status update from stopped poller`);

						return;
					}

					$.set(onTheRunStatusMessages, messages, true);
				},

				onProgress: (progress) => {
					if (!activePollers.has(newPoller)) {
						return;
					}

					$.set(pollProgress, progress, true);
				}
			},
			route.display,
			collectConfiguredStops($.get(config)),
			retryPolicyFromDisplay(route.display)
		);

		$.set(onTheRunPoller, newPoller, true);
		activePollers.add(newPoller);
		await newPoller.start();

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setTimeout(
					() => {
						initializeAll();
					},
					50
				);
			});
		});
	}

	async function handleConfigSave(tomlConfig) {
		// Validation is done in ConfigModal before calling this
		// But we still validate here as a safety check
		const parsed = configParser.parseToml(tomlConfig);

		// Store both: parsed config (for app use) and raw TOML (for editing)
		await configStorage.saveConfig(parsed);

		await configStorage.saveConfigToml(tomlConfig);
		$.set(config, parsed, true);
		$.set(onTheRunConfig, parsed.onTheRun ?? null, true);
		$.set(showConfigModal, false);
		await initializeRoute();
	}

	function handleConfigCancel() {
		$.set(showConfigModal, false);
	}

	function openConfig() {
		$.set(showConfigModal, true);
	}

	function handleRouteChange(path) {
		if (!$.get(config)) {
			return;
		}

		const route = $.get(config).routes.find((r) => r.path === path);

		if (route) {
			switchRoute(route, true); // true = update hash (user explicitly changed route)
		}
	}

	function isOnTheRunRouteActive() {
		return $.get(currentRoute)?.isOnTheRun === true || $.get(currentRoute)?.path === ON_THE_RUN_ROUTE;
	}

	async function handleLocationUpdateClick() {
		if (!$.get(onTheRunPoller)) {
			console.warn("On-the-run poller not available");

			return;
		}

		await $.get(onTheRunPoller).refreshNow();
	}

	var div = root_1();
	let classes;
	var div_1 = $.child(div);
	var h1 = $.child(div_1);
	var text = $.only_child(h1, true);
	var div_2 = $.sibling(h1, 2);
	var node = $.child(div_2);

	{
		var consequent = ($$anchor) => {
			var text_1 = $.text();

			$.template_effect(($0, $1) => $.set_text(text_1, `Last updated: ${$0 ?? ''} ${$1 ?? ''}`), [
				() => formatDate($.get(lastUpdateTime)),
				() => $.get(lastUpdateTime).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
			]);

			$.append($$anchor, text_1);
		};

		$.if(node, ($$render) => {
			if ($.get(lastUpdateTime)) $$render(consequent);
		});
	}

	$.reset(div_2);
	$.reset(div_1);

	var node_1 = $.sibling(div_1, 2);

	{
		var consequent_1 = ($$anchor) => {
			StopFailures($$anchor, {
				get failures() {
					return $.get(stopFailures);
				}
			});
		};

		$.if(node_1, ($$render) => {
			if ($.get(showFailureNotice)) $$render(consequent_1);
		});
	}

	var div_3 = $.sibling(node_1, 2);
	var node_2 = $.child(div_3);

	{
		let $0 = $.derived(() => $.get(currentRoute)?.display);
		let $1 = $.derived(() => isOnTheRunRouteActive() ? $.get(onTheRunStatusMessages) : []);

		DeparturesList(node_2, {
			get groupedDepartures() {
				return $.get(groupedDepartures);
			},

			get unsupportedProviders() {
				return $.get(unsupportedProviders);
			},

			get display() {
				return $.get($0);
			},

			get isPageSuspended() {
				return $.get(isPageSuspended);
			},

			get statusMessages() {
				return $.get($1);
			},

			get progress() {
				return $.get(pollProgress);
			}
		});
	}

	$.reset(div_3);

	var node_3 = $.sibling(div_3, 2);

	{
		let $0 = $.derived(() => $.get(config)?.routes ?? []);
		let $1 = $.derived(() => $.get(currentRoute)?.path ?? null);
		let $2 = $.derived(() => Boolean($.get(onTheRunConfig)));
		let $3 = $.derived(() => !$.get(onTheRunPoller) || !isOnTheRunRouteActive());

		StatusBar(node_3, {
			get apiStatus() {
				return $.get(apiStatus);
			},

			get stopFailures() {
				return $.get(stopFailures);
			},
			onConfigClick: openConfig,
			get routes() {
				return $.get($0);
			},

			get currentRoutePath() {
				return $.get($1);
			},
			onRouteChange: handleRouteChange,
			get refreshIntervalSeconds() {
				return $.get(refreshIntervalSeconds);
			},

			get isFetching() {
				return $.get(isFetching);
			},

			get showLocationUpdate() {
				return $.get($2);
			},
			onLocationUpdateClick: handleLocationUpdateClick,
			get locationUpdateDisabled() {
				return $.get($3);
			}
		});
	}

	var node_4 = $.sibling(node_3, 2);

	{
		var consequent_2 = ($$anchor) => {
			ConfigModal($$anchor, { onSave: handleConfigSave, onCancel: handleConfigCancel });
		};

		$.if(node_4, ($$render) => {
			if ($.get(showConfigModal)) $$render(consequent_2);
		});
	}

	$.reset(div);

	$.template_effect(() => {
		classes = $.set_class(div, 1, 'container', null, classes, {
			'fill-vertical-space': $.get(currentRoute)?.display?.fillVerticalSpace ?? false
		});

		$.set_text(text, $.get(currentRoute)?.display?.title ?? "MVG Departures");
	});

	$.append($$anchor, div);
	$.pop();
}